import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { cartItems, totalPrice, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 flex flex-col transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center space-x-2">
            <span className="w-6 h-6 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line text-xl text-[#8B4513]"></i>
            </span>
            <h2 className="text-lg font-black text-[#1a1a1a]">購物車</h2>
            {cartItems.length > 0 && (
              <span className="bg-[#8B4513] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {cartItems.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <span className="w-20 h-20 flex items-center justify-center mb-4">
                <i className="ri-shopping-cart-2-line text-6xl text-gray-200"></i>
              </span>
              <p className="text-gray-400 text-base font-medium">購物車是空的</p>
              <p className="text-gray-300 text-sm mt-1">快去挑選您喜愛的肉品吧！</p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2.5 bg-[#8B4513] text-white text-sm font-bold rounded-full hover:bg-[#6B3410] transition-colors cursor-pointer whitespace-nowrap"
              >
                繼續購物
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item.product.id}
                  className="flex items-start space-x-3 bg-[#FAF8F5] rounded-xl p-3"
                >
                  <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="w-full h-full object-contain p-1"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-sm font-bold text-[#1a1a1a] leading-tight">
                          {item.product.name}
                        </h4>
                        <p className="text-xs text-gray-400 mt-0.5">{item.product.specs[0]}</p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors cursor-pointer flex-shrink-0 ml-2"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 hover:border-[#8B4513] hover:text-[#8B4513] transition-colors cursor-pointer text-gray-500"
                        >
                          <i className="ri-subtract-line text-xs"></i>
                        </button>
                        <span className="text-sm font-bold text-[#1a1a1a] w-6 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 hover:border-[#8B4513] hover:text-[#8B4513] transition-colors cursor-pointer text-gray-500"
                        >
                          <i className="ri-add-line text-xs"></i>
                        </button>
                      </div>
                      <span className="text-sm font-black text-[#1a1a1a]">
                        NT$ {item.product.price * item.quantity}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-100 px-6 py-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">小計</span>
              <span className="text-sm font-bold text-[#1a1a1a]">NT$ {totalPrice}</span>
            </div>
            <div className="flex items-center justify-between mb-5">
              <span className="text-sm text-gray-500">運費</span>
              <span className="text-sm text-green-600 font-medium">
                {totalPrice >= 1000 ? '免運費' : 'NT$ 100'}
              </span>
            </div>
            <div className="flex items-center justify-between mb-5 pt-3 border-t border-gray-100">
              <span className="text-base font-bold text-[#1a1a1a]">總計</span>
              <span className="text-xl font-black text-[#8B4513]">
                NT$ {totalPrice >= 1000 ? totalPrice : totalPrice + 100}
              </span>
            </div>
            {totalPrice < 1000 && (
              <p className="text-xs text-center text-gray-400 mb-4">
                再購買 NT$ {1000 - totalPrice} 即可享免運費
              </p>
            )}
            <button
              onClick={handleCheckout}
              className="w-full bg-[#8B4513] text-white py-4 rounded-full font-bold text-sm hover:bg-[#6B3410] transition-colors cursor-pointer whitespace-nowrap"
            >
              前往結帳
            </button>
            <button
              onClick={onClose}
              className="w-full mt-2 text-gray-400 text-sm py-2 hover:text-gray-600 transition-colors cursor-pointer"
            >
              繼續購物
            </button>
          </div>
        )}
      </div>
    </>
  );
}
